/**
 * SuperAdminDashboard - Tier 1 dashboard organism for super_admin /
 * developer roles.
 *
 * Shows:
 *   - Period picker (preset + custom range)
 *   - Top metric cards: total WOs, total Inspections, completed in
 *     period (with delta vs comparison), revenue (with delta), and
 *     approved-estimate book total.
 *   - Per-status breakdown for both record types.
 *   - Per-user task aggregation table (open + completed counts).
 *
 * Owned at the organism tier — composed by DashboardPage. Owns no
 * fetch logic itself; the parent passes the metrics + filter state
 * + handlers and this component renders.
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MetricCard } from '../../molecules';
import { DashboardPeriodPicker } from '../DashboardPeriodPicker';
import { InspectionRatingsPanel } from '../InspectionRatingsPanel';
import { formatCurrencyCompact } from '../../../utils/formatCurrency';
import { formatLabel } from '../../../utils/formatLabel';
import type { DashboardFilters, DashboardMetrics } from '../../../services/dashboardService';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface SuperAdminDashboardProps {
    metrics: DashboardMetrics;
    filters: DashboardFilters;
    onFiltersChange: (next: DashboardFilters) => void;
    /** Optional outer className merged onto the root element. */
    className?: string;
}

/* ============================================
 * HELPERS
 * ============================================ */

/** Convert a status slug to a display label (e.g. "in_progress" -> "In Progress").
 *  Shared with the rest of the app via utils/formatLabel — keeps
 *  list / detail / dashboard casing consistent. */
const formatStatus = formatLabel;

/* ============================================
 * COMPONENT
 * ============================================ */

export const SuperAdminDashboard: React.FC<SuperAdminDashboardProps> = ({
    metrics,
    filters,
    onFiltersChange,
    className,
}) => {
    const { counts, revenue, tasksByUser, comparison } = metrics;
    const navigate = useNavigate();

    /* Deep-link into the inspections list pre-filtered to the clicked
     * rating tier. The tiles count the full role-scoped set (the metrics
     * endpoint applies no period filter to them), so a plain rating deep
     * link lands on exactly the rows behind the number — the list page's
     * one-shot params clear its other filters on arrival. */
    const handleSelectRating = (rating: string) => navigate(`/inspections?rating=${rating}`);
    /* The critical callout counts OPEN inspections with critical findings,
     * so pair the critical tier with the non-terminal statuses. */
    const handleSelectCritical = () => navigate('/inspections?rating=critical&status=pending,in_progress,observations_complete,corrections_in_progress');

    /* The active period for the Completed card's deep link. When the picker
     * has an explicit range use it; otherwise mirror the server default
     * (current calendar month, local clock — same math as the picker's
     * This Month preset). */
    const periodRange = useMemo(() => {
        const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (filters.from) return { from: filters.from, to: filters.to ?? '' };
        const today = new Date();
        return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: iso(today) };
    }, [filters.from, filters.to]);

    /* Metric-card deep links. The two totals count the full role-scoped set
     * with no period filter, so their links clear the list filters outright
     * ("?status=" takes over the one-shot filter state and resets every
     * dimension). Completed-in-period counts records whose COMPLETION falls
     * in the period — the list's date range matches on created_at, so the
     * landing rows approximate the count; it also spans both record types,
     * and the link lands on the work-order share. */
    const handleTotalWorkOrders = () => navigate('/work-orders?status=');
    const handleTotalInspections = () => navigate('/inspections?status=');
    const handleCompletedInPeriod = () => navigate(`/work-orders?status=completed,invoiced,closed&date_from=${periodRange.from}&date_to=${periodRange.to}`);

    return (
        <div className={['super-admin-dashboard', 'space-y-24px', className ?? ''].join(' ')} data-ref="organism-super-admin-dashboard">
            {/* Period picker — drives the comparison delta and revenue window. */}
            <div className="super-admin-dashboard__header flex flex-wrap items-center justify-between gap-12px">
                <DashboardPeriodPicker value={filters} onChange={onFiltersChange} />
                {comparison && (
                    <span className="text-12px font-sans text-brand-text-muted">
                        Comparing against <strong>{comparison.label}</strong>
                    </span>
                )}
            </div>

            {/* Top metric cards row — the three count cards click through to
                their pre-filtered lists; the revenue cards stay static. */}
            <div className="super-admin-dashboard__metrics grid grid-cols-1 tablet:grid-cols-3 desktop:grid-cols-5 gap-12px">
                <MetricCard
                    value={counts.workOrders.total}
                    label="Total Work Orders"
                    onClick={handleTotalWorkOrders}
                />
                <MetricCard
                    value={counts.inspections.total}
                    label="Total Inspections"
                    onClick={handleTotalInspections}
                />
                <MetricCard
                    value={counts.completedCurrent}
                    label="Completed (this period)"
                    current={counts.completedCurrent}
                    previous={counts.completedPrevious}
                    deltaMode="absolute"
                    onClick={handleCompletedInPeriod}
                />
                {revenue && (
                    <MetricCard
                        value={formatCurrencyCompact(revenue.current)}
                        label="Revenue (this period)"
                        current={revenue.current}
                        previous={revenue.previous}
                        deltaMode="percent"
                    />
                )}
                {revenue && (
                    <MetricCard
                        value={formatCurrencyCompact(revenue.estimateApprovedTotal)}
                        label={`Approved estimates (${revenue.estimateApprovedCount})`}
                    />
                )}
            </div>

            {/* Per-status breakdown — two columns side by side. Rows deep-link
                into the list pre-filtered to just that status. */}
            <div className="grid grid-cols-1 tablet:grid-cols-2 gap-16px">
                <StatusBreakdown
                    title="Work Orders by Status"
                    statusMap={counts.workOrders.byStatus}
                    onSelectStatus={(status) => navigate(`/work-orders?status=${status}`)}
                />
                <StatusBreakdown
                    title="Inspections by Status"
                    statusMap={counts.inspections.byStatus}
                    onSelectStatus={(status) => navigate(`/inspections?status=${status}`)}
                />
            </div>

            {/* Inspection quality — how sites are scoring, and whether any
                critical finding is sitting open long enough to escalate.
                Tiles click through to the inspections list pre-filtered
                to that rating tier. */}
            <InspectionRatingsPanel
                counts={counts.inspections}
                onSelectRating={handleSelectRating}
                onSelectCritical={handleSelectCritical}
            />

            {/* Per-user task table */}
            {tasksByUser && tasksByUser.length > 0 && (
                <section className="super-admin-dashboard__tasks bg-white border border-brand-border rounded-md overflow-hidden">
                    <header className="px-16px py-12px border-b border-brand-border">
                        <h2 className="text-16px font-sans font-semibold text-brand-text">Tasks by User</h2>
                        <p className="text-12px font-sans text-brand-text-muted mt-2px">
                            Open + completed counts across Work Orders and Inspections.
                        </p>
                    </header>
                    <div className="overflow-x-auto">
                        <table className="w-full text-14px">
                            <thead className="bg-brand-surface text-brand-text-muted text-12px uppercase tracking-wide">
                                <tr>
                                    <th className="text-left px-16px py-10px font-medium">User</th>
                                    <th className="text-right px-16px py-10px font-medium">Open</th>
                                    <th className="text-right px-16px py-10px font-medium">Completed</th>
                                    <th className="text-right px-16px py-10px font-medium">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tasksByUser.map((row) => (
                                    <tr key={row.userId} className="border-t border-brand-border">
                                        <td className="px-16px py-10px text-brand-text font-medium">{row.name}</td>
                                        <td className="px-16px py-10px text-right">{row.open}</td>
                                        <td className="px-16px py-10px text-right">{row.completed}</td>
                                        <td className="px-16px py-10px text-right font-semibold">{row.open + row.completed}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
        </div>
    );
};

/* ============================================
 * Local sub-component: StatusBreakdown
 * ============================================
 * Inlined here rather than promoted to a molecule because it's only
 * used by SuperAdminDashboard + ManagerDashboard (which imports it
 * directly). If a third caller emerges, promote it.
 */
interface StatusBreakdownProps {
    title: string;
    statusMap: Record<string, number>;
    /** When set, rows render as buttons and clicking one reports its
     *  status slug — used to deep-link into the pre-filtered list. */
    onSelectStatus?: (status: string) => void;
}

export const StatusBreakdown: React.FC<StatusBreakdownProps> = ({ title, statusMap, onSelectStatus }) => {
    const rows = Object.entries(statusMap);
    return (
        <section className="bg-white border border-brand-border rounded-md p-16px">
            <h3 className="text-14px font-sans font-semibold text-brand-text mb-12px">{title}</h3>
            {rows.length === 0 ? (
                <p className="text-12px text-brand-text-muted">No records.</p>
            ) : (
                <ul className="space-y-6px">
                    {rows.map(([status, count]) => (
                        <li key={status} className="text-13px">
                            {onSelectStatus ? (
                                <button
                                    type="button"
                                    onClick={() => onSelectStatus(status)}
                                    className="super-admin-dashboard__status-row w-full flex items-center justify-between gap-8px -mx-6px px-6px py-2px min-h-44px desktop:min-h-32px rounded-sm text-left cursor-pointer hover:bg-brand-surface transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                                >
                                    <span className="text-brand-text">{formatStatus(status)}</span>
                                    <span className="font-semibold text-brand-text">{count}</span>
                                </button>
                            ) : (
                                <div className="flex items-center justify-between">
                                    <span className="text-brand-text">{formatStatus(status)}</span>
                                    <span className="font-semibold text-brand-text">{count}</span>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};

export default SuperAdminDashboard;
